import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
	BusinessPartner,
	BusinessPartnerContactPerson,
	EnrichedCartItem,
	OrderBody,
	ShippingType,
	SmartlifeQuery,
} from '@oe-backend/types';
import { catchError, firstValueFrom, of } from 'rxjs';
import { Helper } from 'src/app/helper/helper';
import { OrderService } from 'src/app/services/api/order/order.service';
import { SmartlifeService } from 'src/app/services/api/smartlife/smartlife.service';
import { OrderForm } from 'src/app/types/order.type';
import { UserDataService } from '../../services/user-data/user-data.service';
import { withLoadingState } from '../loading-state/with-loading-state';
import { OrderState } from '../types/order.type';
import { ConfigurationStore } from './configuration.store';
import { ECPStore } from './ecp.store';
import { ProductsStore } from './products.store';

export const OrderStore = signalStore(
	{ providedIn: 'root' },
	withState<OrderState>({
		vrxValues: null,
		order: {},
		selectedCartItemId: null,
		onGoingOrder: false,
	}),
	withLoadingState({ vrxValues: 'init', submit: 'init' }),
	withMethods((state) => {
		const orderService = inject(OrderService);
		const smartLifeService = inject(SmartlifeService);
		const userDataService = inject(UserDataService);
		const ecpStore = inject(ECPStore);
		const configurationStore = inject(ConfigurationStore);

		return {
			submitOrders: async (orders: OrderBody[]) => {
				state.loading('submit');
				try {
					await firstValueFrom(orderService.submit(orders));
					patchState(state, {
						order: {},
						vrxValues: null,
						selectedCartItemId: null,
						onGoingOrder: false,
					});
					state.loaded('submit');
				} catch (error: any) {
					state.errored('submit');
				}
			},
			submitOrdersByIds: async (cartItemIds: string[]) => {
				let submitOrdersResult = null;
				state.loading('submit');
				try {
					submitOrdersResult = await firstValueFrom(orderService.submitByIds(cartItemIds));
					patchState(state, {
						order: {},
						vrxValues: null,
						selectedCartItemId: null,
						onGoingOrder: false,
					});
				} finally {
					state.loaded('submit');
				}
				return submitOrdersResult;
			},
			fetchVrxValues: async (query: SmartlifeQuery) => {
				state.loading('vrxValues');
				// Error handling is done in the interceptor.
				// For that reason, null is returned in case of an error.
				const vrxValues = await firstValueFrom(
					smartLifeService.fetchVrxValues(query).pipe(catchError(() => of(null))),
				);
				patchState(state, { vrxValues });
				state.loaded('vrxValues');
			},
			reset: () => {
				state.isInitial('submit');
				state.isInitial('vrxValues');
				patchState(state, {
					order: {},
					vrxValues: null,
					selectedCartItemId: null,
					onGoingOrder: false,
				});
			},
			resetVrxValues: () => {
				state.isInitial('vrxValues');
				patchState(state, { vrxValues: null });
			},
			setShippingAddress: async (shipTo: BusinessPartner, shippingType: ShippingType) => {
				// Update first shipTo without contactPerson, so the UI can be updated
				patchState(state, {
					order: {
						...state.order(),
						shipTo,
						shippingType,
					},
				});

				if (
					!(
						shippingType === 'B2B' &&
						!shipTo.contactPerson &&
						shipTo.customerNumber &&
						configurationStore.settings()?.show_contact_person
					)
				) {
					return;
				}

				const localDefaultContactPerson = await userDataService.getDefaultContactPerson(
					shipTo.customerNumber,
				);
				await ecpStore.fetchShipToContactPersons(shipTo.customerNumber);
				const contacts = ecpStore.shipToContacts();

				if (!contacts.length) {
					// If there are no contacts, we don't need to set a default contact person
					return;
				}

				let contactPerson = contacts.find(
					(c) => c.contactNumber === localDefaultContactPerson?.contactNumber,
				);

				if (!contactPerson) {
					// Set first contact as default when there is no local default contact
					contactPerson = contacts[0];
				}

				//When rework of store is done, this part could be handled by setContactPerson
				userDataService.setDefaultContactPerson(shipTo.customerNumber, contactPerson);
				patchState(state, {
					order: {
						...state.order(),
						shipTo: {
							...shipTo,
							contactPerson,
						},
					},
				});
			},
			setContactPerson: (contactPerson: BusinessPartnerContactPerson) => {
				const shipTo = state.order().shipTo;
				patchState(state, {
					order: {
						...state.order(),
						shipTo: {
							...shipTo,
							contactPerson,
						},
					},
				});

				if (shipTo?.customerNumber) {
					userDataService.setDefaultContactPerson(shipTo.customerNumber, contactPerson);
				}
			},
			setOnGoingOrder: (onGoingOrder: boolean) => {
				patchState(state, { onGoingOrder });
			},
			setCurrentOrderFromOrderForm: ({
				ageGroup,
				category,
				color,
				engraving,
				prescriptions,
				product,
				reference,
				isTestOrder,
			}: OrderForm) => {
				const soldToStoreQuery = ecpStore.selectedSoldToStoreQuery();
				if (soldToStoreQuery) {
					const order: OrderBody = {
						ageGroup,
						category,
						color: color ? { colorCode: color } : undefined,
						orderType: state.order().orderType || 'ECPOrder',
						personalization: { selected: true, text: engraving || '' },
						prescriptions: prescriptions ? prescriptions : undefined,
						product: product ? product : undefined,
						reference: { text: reference },
						soldTo: soldToStoreQuery,
						isTestOrder,
					};
					patchState(state, { order: { ...state.order(), ...order } });
				}
			},
			setCurrentOrderFromCartItem: ({
				id: selectedCartItemId,
				ageGroup,
				category,
				color,
				personalization,
				prescriptions,
				product,
				reference,
				shipTo,
				shippingType,
				soldTo,
				sendingSystem,
				complete,
				originalOrder,
				isTestOrder,
				smartlife,
				orderType,
				state: cartItemState,
			}: EnrichedCartItem) => {
				const order: OrderBody = {
					ageGroup,
					category: category?.id,
					color,
					orderType,
					personalization,
					prescriptions,
					product: product?.id,
					reference,
					soldTo: soldTo?.customerNumber
						? { zeissCustomerNumber: soldTo?.customerNumber }
						: undefined,
					shipTo,
					shippingType,
					sendingSystem,
					complete,
					originalOrder,
					isTestOrder,
					state: cartItemState,
				};
				patchState(state, { order, selectedCartItemId, vrxValues: smartlife });
			},
		};
	}),
	withComputed(({ vrxValues, order, selectedCartItemId }) => {
		const productsStore = inject(ProductsStore);
		return {
			isVrxValid: computed(() => vrxValues() !== null),
			displayVrx: computed(() => Helper.displayVrx(vrxValues(), order().prescriptions)),
			selectedShipToStore: computed(() => order().shipTo),
			isB2BShipment: computed(() => order().shippingType === 'B2B'),
			product: computed(() => productsStore.products().find((p) => p.id === order().product)),
			selectedProduct: computed(() =>
				order().product != null && order().product! > 1 ? 'reader' : 'prescription',
			),
			isEditMode: computed(() => selectedCartItemId() !== null),
		};
	}),
);
